import AuthenticationContext from 'adal-angular/lib/adal.js'

const config = {
  instance: 'https://login.microsoftonline.com/',
  tenant: process.env.VUE_APP_AUTH_TENANTNAME,
  clientId: process.env.VUE_APP_AUTH_CLIENTID,
  redirectUri: window.location.origin,
  cacheLocation: 'localStorage',
  // Bound ADAL's hidden-iframe renewal so it fails fast (instead of hanging) when the
  // silent-SSO document is blocked by a proxy/network. The JS guard below is a backstop.
  loadFrameTimeout: 5000
};

export default {
  authenticationContext: null,
  // Tracks in-flight renewal promises per resource so concurrent callers share one
  // (single-flight). Without this, several API requests hitting an expired token would
  // each kick off a hidden-iframe renewal, racing each other and the BSSO extension.
  _inFlight: {},
  /**
   * @return {Promise}
   */
  initialize() {
    this.authenticationContext = new AuthenticationContext(config);
    return new Promise((resolve) => {
      if (this.authenticationContext.isCallback(window.location.hash) || window.self !== window.top) {
        // redirect to the location specified in the url params.
        this.authenticationContext.handleWindowCallback();
      }
      else {
        // try pull the user out of local storage
        let user = this.authenticationContext.getCachedUser();

        if (user) {
          resolve();
        }
        else {
          // no user at all - go sign in.
          this.signIn();
        }
      }
    });
  },
  /**
   * Clears only the failed resource's ADAL renewal bookkeeping. A Graph renewal may be
   * in progress alongside an API renewal, so clearing every ADAL renewal would discard
   * the other resource's callback and leave its callers waiting indefinitely
   */
  clearRenewalState(resource) {
    try {
      const context = this.authenticationContext;
      const expectedState = context._activeRenewals[resource];
      context._activeRenewals[resource] = null;

      if (expectedState) {
        context._renewStates = context._renewStates.filter(state => state !== expectedState);
        context._callBackMappedToRenewStates[expectedState] = null;
        context._callBacksMappedToRenewStates[expectedState] = null;
      }

      // Match ADAL's timeout path so a subsequent acquireToken call starts a fresh iframe
      context._saveItem(
        context.CONSTANTS.STORAGE.RENEW_STATUS + resource,
        context.CONSTANTS.TOKEN_RENEW_STATUS_CANCELED
      );
    } catch (e) {
      // ignore
    }
  },
  /**
   * @return {Promise.<String>} A promise that resolves to an ADAL token for resource access
   */
  acquireToken(resource, retry = 1) {
    if (!resource) resource = config.clientId
    // Single-flight: if a renewal for this resource is already running, reuse it instead
    // of starting another iframe. This is also what makes clearRenewalState() safe — the
    // bookkeeping is only ever cleared within this one sequential chain, never across
    // concurrent renewals (which is what previously risked duplicate iframes).
    if (this._inFlight[resource]) return this._inFlight[resource]

    const promise = new Promise((resolve, reject) => {
      const attempt = (attemptsLeft) => {
        // Hard guard: if ADAL's hidden iframe never loads (e.g. a proxy/network blocks the
        // silent-SSO document request), its callback can fail to fire and the promise would
        // hang forever, freezing every awaiting API call. This timeout guarantees we settle.
        let settled = false;
        const guard = setTimeout(() => {
          if (settled) return;
          if (attemptsLeft > 0) {
            this.clearRenewalState(resource);
            return attempt(attemptsLeft - 1);
          }
          settled = true;
          this.clearRenewalState(resource);
          reject('Token renewal timed out (silent SSO iframe unreachable)');
        }, 7000);

        this.authenticationContext.acquireToken(resource, (error, token) => {
          if (settled) return;
          if (error || !token) {
            // ADAL's hidden-iframe renewal frequently times out on the first attempt
            // (Chrome BSSO/ChromeSsoTelemetry "Reload" race). Clear the in-flight renewal
            // state and retry once before giving up.
            const isTimeout = typeof error === 'string' && /timeout/i.test(error);
            if (attemptsLeft > 0 && (isTimeout || !token)) {
              clearTimeout(guard);
              this.clearRenewalState(resource);
              return attempt(attemptsLeft - 1);
            }
            settled = true;
            clearTimeout(guard);
            this.clearRenewalState(resource);
            return reject(error);
          }
          settled = true;
          clearTimeout(guard);
          return resolve(token);
        });
      };
      attempt(retry);
    });

    // Release the single-flight slot once settled so the next expiry can renew again.
    this._inFlight[resource] = promise;
    const clearInFlight = () => { delete this._inFlight[resource]; };
    promise.then(clearInFlight, clearInFlight);
    return promise;
  },
  /**
   * Issue an interactive authentication request for the current user and the api resource.
   */
  acquireTokenRedirect() {
    this.authenticationContext.acquireTokenRedirect(config.clientId);
  },
  /**
   * @return {Boolean} Indicates if there is a valid, non-expired access token present in localStorage.
   */
  isAuthenticated() {
    // getCachedToken will only return a valid, non-expired token.
    if (this.authenticationContext.getCachedToken(config.clientId)) { return true; }
    return false;
  },
  /**
   * Synchronously returns a still-valid cached access token (or null) without
   * triggering the hidden-iframe renewal. Use this as a fast path so normal API
   * calls never wait on the network when a valid token is already cached.
   * @return {String|null}
   */
  getCachedToken(resource) {
    if (!resource) resource = config.clientId;
    return this.authenticationContext.getCachedToken(resource) || null;
  },
  /**
   * @return An ADAL user profile object.
   */
  getUserProfile() {
    return this.authenticationContext.getCachedUser().profile;
  },
  signIn() {
    this.authenticationContext.login();
  },
  signOut() {
    this.authenticationContext.logOut();
  },
  getTokenExpiration() {
    if (!this.isAuthenticated()) return null;
    return this.authenticationContext?._user?.profile?.exp;
  },
  /**
   * Reads the access-token expiration from ADAL's localStorage cache. This value
   * is shared across all browser tabs, so every tab observes a renewal performed in
   * any other tab. Returns the expiration as epoch seconds, or null when unavailable.
   * @return {Number|null}
   */
  getCachedTokenExpiration(resource) {
    try {
      const ctx = this.authenticationContext;
      if (!ctx) return null;
      if (!resource) resource = config.clientId;
      const value = ctx._getItem(ctx.CONSTANTS.STORAGE.EXPIRATION_KEY + resource);
      return value ? Number(value) : null;
    } catch (e) {
      return null;
    }
  }
}